import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import AddIcon from '@material-ui/icons/Add'
import Button from '@material-ui/core/Button'
import DeleteIcon from '@material-ui/icons/Delete'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import { withStyles } from '@material-ui/core/styles'
import { NominationActions, SnackbarActions, AllocationActions } from 'actionsets'
import Table, { TableBody, TableRow, TableCell } from 'components/CollapsibleTable'
import { Pagination } from 'components'
import Dependent from 'containers/shared/Dependent'
import { NominationDialog } from 'containers/nominations'
import AllocationForm from './AllocationForm'
import { Authorization, compose } from 'utils'
import { connectQueryString } from 'containers/shared'
import { SortedTableHead } from 'components'
import { withRouter } from 'react-router-dom'
import {NumberDisplay} from 'components/numbers'
import {capitalize} from 'utils'

export class NominationsList extends Component {

  static propTypes = {
    dealSheet: PropTypes.object.isRequired,
    nominations: PropTypes.array.isRequired,
    selection: PropTypes.object.isRequired,
    page: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
  }

  static defaultProps = {
    pageSize: 10
  }

  constructor(props) {
    super(props)
    NominationActions.bindActions(this)
    AllocationActions.bindActions(this, 'allocations')
    SnackbarActions.bindActions(this, 'snackbar')
    this.state = { creatingNewNomination: false, page: 1 }
  }

  dependsOn = () => {
    return this.loadData()
  }

  dependenciesMet = () => {
    return this.props.requests.length === 0
  }

  componentDidUpdate(prevProps) {
    if (prevProps.query !== this.props.query)
      this.loadData()
  }

  loadData = async () => {
    const { dealSheet } = this.props
    await this.actions.index(dealSheet.id, this.props.page, this.props.order)
  }

  nominationStatus({status}) {
    switch (status) {
      case 'membership_pending':
        return `User membership pending`
      case 'investor_account_pending':
        return `Investor account awaiting approval`
      case 'good_to_go':
        return `Ready to be actioned upon`
      default:
        return ''
    }
  }

  get isProduct() {
    return this.props.dealSheet.typeOfIssuance === 'product'
  }

  get nominationName() {
    return this.isProduct ? 'RFQ' : 'nomination'
  }

  get allocationName() {
    return this.isProduct ? 'Quote' : 'allocation'
  }

  handleNominationDialogOpened = () => {
    this.setState({creatingNewNomination: true})
  }

  handleNominationDialogClosed = () => {
    this.setState({creatingNewNomination: false})
  }

  handleNominationAdded = () => {
    this.handleNominationDialogClosed()
    this.loadData()
  }

  handleNominationDelete = ({id})  => async () => {
    try {
      await this.actions.destroy({ id })
      this.loadData()
      this.actions.snackbar.success(`${capitalize(this.nominationName)} deleted!`)
    }catch(error) {
      this.actions.snackbar.error(`Failed to delete ${this.nominationName}!`)
    }
  }

  handleNominationTranstionToAllocation = nomination => () => {
    this.setState({allocating: true})
    this.actions.allocations.setNewInvestorIds({ids: [nomination.userId], params: {
      amount: parseFloat(nomination.amount || (this.props.dealSheet.preSaleRate * nomination.value)),
      amountOf: nomination.amount ? 'units' : 'value',
      rate: parseFloat(this.props.dealSheet.preSaleRate),
      thirdPartyCommissions: []
    }})
  }

  handleNominationTransitionComplete = () => {
    this.setState({allocating: false}, this.loadData)
  }

  renderCreateButton = () => {
    if(!Authorization.issuer && !Authorization.admin)
      return false

    return (
      <Fragment>
        <div className={this.props.classes.filterBar}>
          <div></div>
          <Tooltip title={`Create a new ${this.nominationName}`}>
            <Button variant='contained' color="primary" onClick={this.handleNominationDialogOpened}>Create</Button>
          </Tooltip>
        </div>
        <NominationDialog
          dealSheet={ this.props.dealSheet }
          onClose={this.handleNominationDialogClosed}
          onNominationAdded={this.handleNominationAdded}
          submitRef={formHandler => this.setState({formHandler})}
          open={this.state.creatingNewNomination}
          title={`Create ${capitalize(this.nominationName)}`}
        />
        {this.state.allocating && <AllocationForm onDone={this.handleNominationTransitionComplete}/>}
      </Fragment>
    )
  }

  renderNomination = (nomination) => {
    const { classes } = this.props
    const { currency } = this.props.dealSheet
    const ticker = this.props.dealSheet.ticker || 'units'
    const { investorAccountName, email, amount, value, status } = nomination

    return (
      <TableRow key={nomination.id} className={classes.row}>
        <TableCell mobileLabel='Investor'>{ investorAccountName || email }</TableCell>
        <TableCell mobileLabel='Amount'><NumberDisplay>{amount}</NumberDisplay> {!!amount && ticker}</TableCell>
        <TableCell mobileLabel='Value'><NumberDisplay>{value}</NumberDisplay> {!!value && currency.code}</TableCell>
        <TableCell mobileLabel='Status'>{this.nominationStatus(nomination)}</TableCell>
        <TableCell className={classes.actionCell}>
            <Tooltip title={`Remove ${this.nominationName}`}>
              <IconButton onClick={this.handleNominationDelete(nomination)} className={classes.iconButton}>
                <DeleteIcon/>
              </IconButton>
            </Tooltip>
            {
              Authorization.admin && status === 'good_to_go' &&
              <Tooltip title={`Create ${this.allocationName} from ${this.nominationName}`}>
                <IconButton onClick={this.handleNominationTranstionToAllocation(nomination)} className={classes.iconButton}>
                  <AddIcon/>
                </IconButton>
              </Tooltip>
            }
        </TableCell>
      </TableRow>
    )
  }

  render = () => {
    const { classes, nominations, totalPages, page, onPageChange, order, onOrderChange } = this.props

    return (
      <Fragment>
        { this.renderCreateButton() }
        <Table className={classes.root}>
          <SortedTableHead order={order} onOrderChange={onOrderChange}>
            <TableRow>
              <TableCell orderBy="name">Investor</TableCell>
              <TableCell orderBy="amount">Amount</TableCell>
              <TableCell orderBy="value">Value</TableCell>
              <TableCell>Status</TableCell>
              {
                Authorization.admin &&
                <TableCell>{/*Actions*/}</TableCell>
              }
            </TableRow>
          </SortedTableHead>
          <TableBody>
            { nominations.map(this.renderNomination) }
          </TableBody>
        </Table>
        <Pagination totalPages={totalPages} page={page} onPageSelected={onPageChange} />
      </Fragment>
    )
  }
}

const styles = theme => ({
  row: {
    '& td': {
      height: '40px',
    }
  },
  actionCell: {
    textAlign: 'right'
  },
  iconButton: {
    width: '40px',
    height: '40px',
  },
  filterBar: {
    display: 'flex',
    justifyContent: 'space-between',
  }
})

export default compose(
  Dependent({loader: true}),
  connectQueryString,
  withRouter,
  connect(({nominations}) => { return { ...nominations } }),
  withStyles(styles, {name: 'NominationsList'}),
)(NominationsList)